'use client'

import React, { useState, useEffect, ReactElement } from 'react'
import { Button, OverlayArrow, Tooltip, TooltipTrigger } from 'react-aria-components'
import { useSearchParams } from 'next/navigation'
import { useUser } from '@auth0/nextjs-auth0'
import useSWR from 'swr'

import Icon from '@/components/ui/icon'

// Props for bookmark button child components - all optional
export type BookmarkButtonProps = {
  postId?: number | string
  savedItem?: boolean
  onBookmark?: (postId: number | string) => void
}

type BookmarkActionProps = {
  postId: number | string
  children: ReactElement<BookmarkButtonProps>
}

const fetcher = (url: string) => fetch(url).then((res) => res.json())

const btnClass = `
  inline-flex
  h-40
  w-40
  items-center
  justify-center
  rounded-sm
  text-fg-title
  hover:bg-theme-accent
  transition-colors
  duration-200
  ease-in-out
`

export function BookmarkAction({ postId, children }: BookmarkActionProps) {
  const [savedItem, setSavedItem] = useState<boolean>(false)
  const { user, isLoading } = useUser()

  const userEmail = user?.email
  const userBookmarks = user?.meta?.saved_items
  const shouldFetch = !isLoading && userEmail
  const url = shouldFetch ? `/api/get-saved-items` : null

  useEffect(() => {
    if (userBookmarks?.includes(postId)) {
      setSavedItem(true)
    }
  }, [userBookmarks, postId])

  const { mutate } = useSWR(url, fetcher)

  async function handleBookmark(postId: number | string) {
    try {
      const response = await fetch('/api/set-saved-items', {
        method: 'POST',
        body: JSON.stringify({
          postId: postId,
        }),
      })

      if (!response.ok) {
        throw new Error(`Could not update saved item: ${postId}`)
      }

      setSavedItem((prev) => !prev)
      mutate()
    } catch (error) {
      console.error('Error updating bookmark:', error)
    }
  }

  // If this page has no postId, return null
  if (!postId || !userEmail) {
    return null
  }

  const childProps: BookmarkButtonProps = {
    postId,
    savedItem,
    onBookmark: handleBookmark,
  }

  return (
    <TooltipTrigger delay={0}>
      {/* Clone children and pass in the necessary props */}
      {React.cloneElement(children, childProps)}
      <Tooltip>
        <OverlayArrow>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <path d="M0 0 L4 4 L8 0" />
          </svg>
        </OverlayArrow>
        <span className="whitespace-nowrap">
          {savedItem ? 'Remove from dashboard' : 'Save to dashboard'}
        </span>
      </Tooltip>
    </TooltipTrigger>
  )
}

export function BookmarkButtonDefault(props: BookmarkButtonProps) {
  const { postId, savedItem = false, onBookmark } = props

  if (!postId || !onBookmark) return null

  return (
    <Button className={btnClass} data-post-id={postId} onPress={() => onBookmark(postId)}>
      <svg
        className={`pointer-events-none h-24 w-24 ${savedItem ? 'fill-primary text-primary' : 'fill-transparent text-current'}`}
        aria-hidden="true">
        <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-bookmark`}></use>
      </svg>
    </Button>
  )
}

export function BookmarkButtonDashboard(props: BookmarkButtonProps) {
  const params = useSearchParams()

  const { postId = false, onBookmark } = props

  // TODO: (JP) this should only show on the dashboard saved items
  if (!postId || !onBookmark || !params.toString().includes('saved')) {
    return null
  }

  return (
    <Button className={btnClass} data-post-id={postId} onPress={() => onBookmark?.(postId)}>
      <Icon id="icon-panel-close" className="h-[14px] w-[14px] text-primary" />
    </Button>
  )
}
